import React, { useEffect, useState } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import Filters from './components/Filters';
import {
  exportSpins,
  fetchCampaigns,
  fetchSpins,
  setFilters,
  voidSpin,
} from '../../redux/features/magicLampSlice';

/**
 * Magic Lamp — the spin log
 *
 * Every spin, filterable by campaign, date and result. Cancelled spins stay
 * visible, struck through, with the reason and who cancelled them shown inline
 * rather than behind a button — an admin scanning the log for what happened
 * should not have to click each row to find out.
 */
const PAGE_SIZE = 25;

export default function SpinLog() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Filters come from the slice, so a range picked on the analytics screen is
  // still applied here.
  const {
    campaigns,
    filters,
    spins,
    spinsTotal,
    loadingSpins: loading,
  } = useSelector((state) => state.magicLamp);

  const [page, setPage] = useState(0);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!campaigns.length) dispatch(fetchCampaigns());
  }, [dispatch, campaigns.length]);

  const load = () =>
    dispatch(fetchSpins({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }))
      .unwrap()
      .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));

  useEffect(() => {
    dispatch(fetchSpins({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }))
      .unwrap()
      .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
  }, [
    dispatch,
    page,
    filters.campaignId,
    filters.from,
    filters.to,
    filters.result,
    enqueueSnackbar,
  ]);

  // Any filter change puts you back on page 1 — staying on page 4 of a
  // different result set is disorienting.
  const changeFilters = (next) => {
    dispatch(setFilters(next));
    setPage(0);
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      await dispatch(voidSpin({ id: cancelling.id, reason: reason.trim() })).unwrap();
      enqueueSnackbar('Spin cancelled', { variant: 'success' });
      setCancelling(null);
      setReason('');
      load();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      await dispatch(exportSpins(filters)).unwrap();
    } catch (error) {
      enqueueSnackbar(error.message || 'Could not export', { variant: 'error' });
    }
  };

  return (
    <GlobalWrapper title="Spin log">
      <Filters
        campaigns={campaigns}
        filters={filters}
        onChange={changeFilters}
        extra={
          <>
            <TextField
              select
              size="small"
              label="Result"
              value={filters.result || ''}
              onChange={(event) => changeFilters({ ...filters, result: event.target.value })}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All results</MenuItem>
              <MenuItem value="win">Wins only</MenuItem>
              <MenuItem value="no_win">No win</MenuItem>
            </TextField>
            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>
              Export CSV
            </Button>
          </>
        }
      />

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={180}>When</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Result</TableCell>
              <TableCell width={110} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!loading && spins.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No spins match these filters.
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              spins.map((spin) => (
                <TableRow
                  key={spin.id}
                  sx={{ bgcolor: spin.status === 'voided' ? 'rgba(254,226,226,0.4)' : undefined }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {new Date(spin.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{spin.campaignName}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {spin.result === 'win' ? (
                        <Typography
                          fontWeight={600}
                          color={spin.status === 'voided' ? 'text.disabled' : 'success.main'}
                          sx={{
                            textDecoration: spin.status === 'voided' ? 'line-through' : 'none',
                          }}
                        >
                          {spin.prize}
                        </Typography>
                      ) : (
                        <Typography color="text.disabled">no win</Typography>
                      )}
                      {spin.status === 'voided' && (
                        <Chip size="small" color="error" variant="outlined" label="cancelled" />
                      )}
                    </Stack>

                    {spin.status === 'voided' && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        <em>&ldquo;{spin.voidReason || 'no reason recorded'}&rdquo;</em>
                        {spin.voidedBy && ` · by ${spin.voidedBy}`}
                        {spin.voidedAt && ` · ${new Date(spin.voidedAt).toLocaleString()}`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {spin.status === 'active' && (
                      <Button size="small" color="error" onClick={() => setCancelling(spin)}>
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={spinsTotal}
          page={page}
          onPageChange={(event, next) => setPage(next)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>

      <Dialog open={Boolean(cancelling)} onClose={() => setCancelling(null)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={800}>Cancel this spin?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The spin stays in the log, marked as cancelled, and stops counting towards
            any of the figures on the analytics screen.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
            placeholder="Spun by accident"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText="Recorded against your name."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelling(null)}>Keep it</Button>
          <Button
            variant="contained"
            color="error"
            disabled={reason.trim().length < 3 || busy}
            onClick={confirmCancel}
          >
            {busy ? 'Cancelling…' : 'Cancel spin'}
          </Button>
        </DialogActions>
      </Dialog>

    </GlobalWrapper>
  );
}